import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';

interface GeneratePeriodsDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (year: number, periodType: string) => Promise<void>;
}

const GeneratePeriodsDialog: React.FC<GeneratePeriodsDialogProps> = ({
  open,
  onClose,
  onGenerate
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear + 1);
  const [periodType, setPeriodType] = useState<string>('Yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      await onGenerate(year, periodType);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate periods');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isRTL ? 'توليد فترات مالية' : 'Generate Fiscal Periods'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          label={isRTL ? 'السنة' : 'Year'}
          type="number"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          sx={{ mt: 2, mb: 2 }}
          inputProps={{ min: 2000, max: 2100 }}
        />

        <FormControl fullWidth>
          <InputLabel>{isRTL ? 'نوع الفترة' : 'Period Type'}</InputLabel>
          <Select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            label={isRTL ? 'نوع الفترة' : 'Period Type'}
          >
            <MenuItem value="Yearly">
              {isRTL ? 'سنوية (1 فترة)' : 'Yearly (1 period)'}
            </MenuItem>
            <MenuItem value="Quarterly">
              {isRTL ? 'ربع سنوية (4 فترات)' : 'Quarterly (4 periods)'}
            </MenuItem>
            <MenuItem value="Monthly">
              {isRTL ? 'شهرية (12 فترة)' : 'Monthly (12 periods)'}
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {isRTL ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button onClick={handleGenerate} variant="contained" disabled={loading}>
          {loading ? (isRTL ? 'جاري التوليد...' : 'Generating...') : (isRTL ? 'توليد' : 'Generate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneratePeriodsDialog;